import { makeWidgetProps, asWidgetProps } from 'Src/types/widget-helpers';
import React, { ComponentProps } from 'react';
import { ICONS } from 'Components/utils/icon/icons';
import { t } from 'Src/i18n';
import { capitalizeWordFirstCaracter } from 'Utils/string/stringUtils';
import { useAppContext } from 'Context/ReactAppContext';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';

import { Capsule } from 'Components/frameworks/capsule/Capsule';
import { Icon } from 'Components/frameworks/icon/Icon';
type Props = {
  className?: string;
  onClickEdit?: () => void;
  user: ProfileUserInfo;
};
export const UserInfoSummary = ({ className, onClickEdit, user }: Props) => {
  const appContext = useAppContext();
  if (!user) {
    return null;
  }

  const shouldShowPhoneWithSeparatePhoneCodeField =
    isFeatureFlipped(appContext)('booking.participants.phone.showWithSeparatePhoneCodeField') ??
    false;

  const {
    address = makeWidgetProps<NonNullable<ProfileUserInfo['address']>>(user.address ?? {}),
    civility,
    email,
    firstName,
    lastName,
    mobilePhoneNumber,
    unicodeFirstName,
    unicodeLastName,
  } = user;

  const mobileCode = (user as any).mobileCode;

  const { city, stateOrDistrict, streetName, streetNumber, zipCode } = address;
  const hasUnicode = !!unicodeFirstName && !!unicodeLastName;
  const formatedCivility = civility ? `${civility}. ` : '';
  const names = hasUnicode
    ? [unicodeFirstName, unicodeLastName]
    : [capitalizeWordFirstCaracter(firstName), capitalizeWordFirstCaracter(lastName)];
  const name = [formatedCivility, ...names].join(' ');

  // Format phone number for display
  const displayPhoneNumber =
    shouldShowPhoneWithSeparatePhoneCodeField && mobileCode && mobilePhoneNumber
      ? `+${mobileCode} ${mobilePhoneNumber}`
      : mobilePhoneNumber || '';

  return (
    <div className={className}>
      <Capsule
        {...asWidgetProps<ComponentProps<typeof Capsule>>({
          header: 'none',
          dataTestId: 'PreCompletedForm',
        })}
      >
        <div className="air360-replay-ignore flex px-10 md:px-5" data-cs-mask>
          <Icon
            {...asWidgetProps<ComponentProps<typeof Icon>>({
              className: 'm-20 hidden text-sienna md:block',
              name: ICONS.ICON_45,
              width: '2.375rem',
            })}
          />

          <div className="pb-15 pt-10">
            <div className="flex flex-col">
              <strong>{name}</strong>
              <span className="me-30">{email || ''}</span>
              <span>{displayPhoneNumber}</span>
              <span className="me-25">{`${streetNumber || ''} ${streetName || ''}`}</span>
              <span>{`${zipCode || ''} ${stateOrDistrict || ''} ${city || ''}`}</span>
            </div>
          </div>
        </div>
      </Capsule>
      {onClickEdit && (
        <div className="mt-10 flex justify-end">
          <button className="cursor-pointer text-14 underline" onClick={onClickEdit} type="button">
            {t('common.edit')}
          </button>
        </div>
      )}
    </div>
  );
};
